import {
	Briefcase,
	Building2,
	CalendarPlus,
	CalendarX2,
	Copy,
	MessageSquare,
	Phone,
	User,
	Users,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { routes } from "../../../configs/constants/routes";
import { useModalStore } from "../../../hooks/modal.hooks";
import {
	useToggleConsultantTag,
	useUserContactSummary,
} from "../../../networking/consultant.hooks";
import {
	useCustomer,
	useCustomerUsersByCustomer,
	useToggleCustomerTag,
} from "../../../networking/customer.hooks";
import { useJob, useToggleJobTag } from "../../../networking/job.hooks";
import { useOrganisation } from "../../../networking/organisation.hooks";
import {
	removeConsultantTag,
	renewConsultantTag,
	toggleConsultantTag,
} from "../../../utils/credential.utils";
import { customerRoute, formatRoute, jobRoute } from "../../../utils/route.utils";
import { toast } from "../../../utils/toast";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import TagToggleSubMenu from "../TagMenu/TagToggleSubMenu";

// TODO: Support customer user

type MarkerKind = "contact" | "customer" | "job";

/**
 * Resolve which marker a right-click hits: the innermost element carrying any
 * of the `data-*` markers. Ties (one element carrying several markers) go in
 * declared order: contact, then customer, then job.
 */
function resolveMarker(target: Element): { kind: MarkerKind; id: string; el: Element } | undefined {
	const candidates = (["contact", "customer", "job"] as MarkerKind[])
		.map((kind) => {
			const el = target.closest(`[data-${kind}]`);
			const id = el?.getAttribute(`data-${kind}`);
			return el && id ? { kind, id, el } : undefined;
		})
		.filter((c): c is { kind: MarkerKind; id: string; el: Element } => !!c);

	// The innermost candidate is the one every other candidate contains.
	return candidates.find((c) => candidates.every((other) => other.el.contains(c.el)));
}

/**
 * Global contact context menu.
 *
 * Mounted once (in App.tsx) and driven by a single document-level `contextmenu`
 * listener: right-clicking any element carrying a `data-contact={userId}` (and an
 * optional `data-date`) attribute opens this menu at the cursor. Rather than wiring
 * a Radix trigger onto each of the ~20 producers, we keep the global listener and
 * render the shadcn DropdownMenu in controlled mode, anchored to an invisible
 * cursor-positioned trigger.
 *
 * Elements carrying `data-customer={customerId}` (e.g. customer list rows, the
 * job details customer link) open a customer variant of the menu instead, with a
 * link to the customer card, a create-job link (preselects the customer via
 * ?customer=<id>), a lazily-loaded "Kontaktpersoner" submenu
 * (call/copy-phone/copy-email per contact person) and — when the org defines
 * customer tag types — a tag toggle submenu.
 *
 * Elements carrying `data-job={jobId}` (job list rows, global-search job hits,
 * the job details sidebar) open a job variant: link to the job, copy job,
 * link to the customer and — when the org defines job tag types — a toggle
 * submenu for the job's DESCRIPTIVE tags (`job.tags`, org `jobTagTypes`; not
 * the matching-gate `requirements.tags`).
 *
 * When several markers nest (e.g. the customer name inside the job sidebar),
 * the innermost one wins.
 */

const ContactContextMenu: React.FC = () => {
	const { t } = useTranslation();

	const [pos, setPos] = useState<{ top: number; left: number }>();
	const [userId, setUserId] = useState("");
	const [customerId, setCustomerId] = useState("");
	const [jobId, setJobId] = useState("");
	// Contact persons load lazily — only once the "Kontaktpersoner" submenu is
	// actually opened, not on every right-click. Reset per menu open.
	const [contactsRequested, setContactsRequested] = useState(false);
	const [day, setDay] = useState("");

	const { toggleAbsenceModal, toggleMessageModal } = useModalStore((state) => ({
		toggleAbsenceModal: state.toggleAbsenceModal,
		toggleMessageModal: state.toggleMessageModal,
	}));

	const { contactSummary, isLoading, refetch } = useUserContactSummary(userId);
	const { customer, isLoading: isCustomerLoading } = useCustomer(customerId || undefined);
	const { customerUsers } = useCustomerUsersByCustomer(
		{ customerId, sort: "firstName" },
		!!customerId && contactsRequested
	);
	const { job, isLoading: isJobLoading } = useJob({
		jobId: jobId || undefined,
		expand: ["customer"],
	});
	const { mutate: toggleTag } = useToggleConsultantTag();
	const { mutate: toggleCustomerTag } = useToggleCustomerTag();
	const { mutate: toggleJobTag } = useToggleJobTag();
	const { data: organisation } = useOrganisation();

	const consultantTagTypes = organisation?.consultantTagTypes ?? [];
	const customerTagTypes = organisation?.customerTagTypes ?? [];
	const jobTagTypes = organisation?.jobTagTypes ?? [];

	useEffect(() => {
		function listener(event: MouseEvent) {
			const marker = resolveMarker(event.target as Element);
			if (!marker) return;

			event.preventDefault();
			// Client coordinates because the anchor below is `position: fixed`.
			setPos({ top: event.clientY, left: event.clientX });
			setContactsRequested(false);

			if (marker.kind === "job") {
				setJobId(marker.id);
				setCustomerId("");
				setUserId("");
			} else if (marker.kind === "customer") {
				setCustomerId(marker.id);
				setJobId("");
				setUserId("");
			} else {
				setUserId(marker.id);
				setCustomerId("");
				setJobId("");
				setDay(marker.el.getAttribute("data-date") || "");
				refetch();
			}
		}

		document.addEventListener("contextmenu", listener);
		return () => document.removeEventListener("contextmenu", listener);
	});

	function close() {
		setPos(undefined);
	}

	function getProfileRoute() {
		if (contactSummary?.role === "customer_user") {
			// Just pick the first customer for now, will still end up at the correct profile
			const customerId = contactSummary.customers ? contactSummary.customers[0].id : "";
			return formatRoute([routes.CUSTOMER_USER_EDIT].join(""), [customerId, userId]);
		} else if (contactSummary?.isCandidate) {
			return formatRoute(routes.RECRUITMENT_DETAILS + routes.CONSULTANTS_PROFILE, [userId]);
		} else {
			return formatRoute(routes.CONSULTANTS_DETAILS + "/" + routes.CONSULTANTS_PROFILE, [userId]);
		}
	}

	function handleOpenAbsenceModal() {
		toggleAbsenceModal({
			consultantId: userId,
			title: contactSummary?.firstName + " " + contactSummary?.lastName,
			selectedDate: day,
		});
	}

	function handleOpenMessageModal() {
		toggleMessageModal({
			consultantId: userId,
			title: contactSummary?.firstName + " " + contactSummary?.lastName,
		});
	}

	function handleToggleTag(key: string) {
		const type = consultantTagTypes.find((tt) => tt.key === key);
		if (!type) return;

		// Optimistic: the mutation patches the contact-summary cache in onMutate,
		// so the open submenu's checkboxes (and the list's tag chips) flip
		// instantly — no refetch needed. Rapid toggles compose because each `next`
		// is computed from the already-patched cache.
		toggleTag({
			userId,
			nextTags: toggleConsultantTag(contactSummary?.tags ?? [], type),
		});
	}

	// Re-marking a held timed tag restarts its clock instead of removing it;
	// removal is its own menu item (see TagToggleSubMenu).
	function handleRenewTag(key: string) {
		const type = consultantTagTypes.find((tt) => tt.key === key);
		if (!type) return;

		toggleTag({ userId, nextTags: renewConsultantTag(contactSummary?.tags ?? [], type) });
	}

	function handleRemoveTag(key: string) {
		toggleTag({ userId, nextTags: removeConsultantTag(contactSummary?.tags ?? [], key) });
	}

	// Customer tags are descriptive labels without expiry — toggling adds or
	// removes the key (keys reference the org's customerTagTypes). Optimistic:
	// the mutation patches the customer caches in onMutate, so the open
	// submenu's checkboxes (and the list's tag chips) flip instantly.
	function handleToggleCustomerTag(key: string) {
		const current = customer?.tags ?? [];
		const isAssigned = current.some((tag) => tag.key === key);
		const nextTags = isAssigned ? current.filter((tag) => tag.key !== key) : [...current, { key }];

		toggleCustomerTag({ customerId, nextTags });
	}

	// Job tags are descriptive labels without expiry — toggling adds or removes
	// the key (keys reference the org's jobTagTypes). Optimistic: the mutation
	// patches the job caches in onMutate, so the open submenu's checkboxes (and
	// the list's tag chips) flip instantly.
	function handleToggleJobTag(key: string) {
		const current = job?.tags ?? [];
		const isAssigned = current.some((tag) => tag.key === key);
		const nextTags = isAssigned ? current.filter((tag) => tag.key !== key) : [...current, { key }];

		toggleJobTag({ jobId, nextTags });
	}

	async function copyToClipboard(value: string) {
		try {
			await navigator.clipboard.writeText(value);
			toast.success(t("contactMenu.copied"));
		} catch {
			// Clipboard unavailable (e.g. insecure context) — silently ignore.
		}
	}

	// Show the tags submenu whenever the org defines at least one consultant tag
	// type. Assigned tags default to [] (the contact-summary omits the field when
	// none are assigned), so a consultant without any tags can still be tagged.
	const canShowTags = contactSummary?.role === "consultant" && consultantTagTypes.length > 0;

	return (
		<DropdownMenu open={!!pos} onOpenChange={(open) => !open && close()}>
			<DropdownMenuTrigger asChild>
				<span
					aria-hidden
					className="pointer-events-none fixed h-0 w-0"
					style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
				/>
			</DropdownMenuTrigger>

			<DropdownMenuContent
				align="start"
				sideOffset={4}
				className="w-60"
				// Let a follow-up modal (message/absence) take focus instead of Radix
				// returning it to the (invisible) trigger.
				onCloseAutoFocus={(event) => event.preventDefault()}
			>
				{jobId ? (
					isJobLoading || !job ? (
						<div className="flex justify-center px-2 py-3">
							<LoadingIndicator size="s" showText={false} />
						</div>
					) : (
						<>
							<DropdownMenuLabel className="flex items-center justify-between gap-4">
								<span className="truncate">{job.title || job.customer?.name}</span>
								<span className="shrink-0 text-xs font-normal text-muted-foreground">
									#{job.orderNumber}
								</span>
							</DropdownMenuLabel>

							<DropdownMenuSeparator />

							<DropdownMenuItem asChild>
								<a href={jobRoute(jobId)}>
									<Briefcase />
									<span>{t("contactMenu.gotoJob")}</span>
								</a>
							</DropdownMenuItem>

							<DropdownMenuItem asChild>
								<a href={`${routes.JOB_CREATE}?copy=${jobId}`}>
									<Copy />
									<span>{t("job.copy")}</span>
								</a>
							</DropdownMenuItem>

							{job.customer?.id && (
								<DropdownMenuItem asChild>
									<a href={customerRoute(job.customer.id)}>
										<Building2 />
										<span>{t("contactMenu.gotoCustomer")}</span>
									</a>
								</DropdownMenuItem>
							)}

							{jobTagTypes.length > 0 && (
								<TagToggleSubMenu
									label={t("contactMenu.tags")}
									tagTypes={jobTagTypes}
									assignedTags={job.tags ?? []}
									onToggle={handleToggleJobTag}
								/>
							)}
						</>
					)
				) : customerId ? (
					isCustomerLoading || !customer ? (
						<div className="flex justify-center px-2 py-3">
							<LoadingIndicator size="s" showText={false} />
						</div>
					) : (
						<>
							<DropdownMenuLabel className="flex items-center justify-between gap-4">
								<span className="truncate">{customer.name}</span>
								{customer.customerNumber ? (
									<span className="shrink-0 text-xs font-normal text-muted-foreground">
										#{customer.customerNumber}
									</span>
								) : null}
							</DropdownMenuLabel>

							<DropdownMenuSeparator />

							<DropdownMenuItem asChild>
								<a href={customerRoute(customerId)}>
									<Building2 />
									<span>{t("contactMenu.gotoCustomer")}</span>
								</a>
							</DropdownMenuItem>

							<DropdownMenuItem asChild>
								<a href={`${routes.JOB_CREATE}?customer=${customerId}`}>
									<CalendarPlus />
									<span>{t("contactMenu.createJob")}</span>
								</a>
							</DropdownMenuItem>

							<DropdownMenuSub onOpenChange={(open) => open && setContactsRequested(true)}>
								<DropdownMenuSubTrigger>
									<Users />
									<span>{t("contactMenu.contactPersons")}</span>
								</DropdownMenuSubTrigger>
								<DropdownMenuSubContent className="w-64">
									{!customerUsers ? (
										<div className="flex justify-center px-2 py-3">
											<LoadingIndicator size="s" showText={false} />
										</div>
									) : customerUsers.length === 0 ? (
										<DropdownMenuItem disabled>
											{t("contactMenu.noContactPersons")}
										</DropdownMenuItem>
									) : (
										customerUsers.map((contact) => {
											const name =
												[contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
												contact.email ||
												contact.id;
											const phone = contact.profile?.phoneNumber;
											const email = contact.email;

											return (
												<DropdownMenuSub key={contact.id}>
													<DropdownMenuSubTrigger>
														<span className="truncate">{name}</span>
													</DropdownMenuSubTrigger>
													<DropdownMenuSubContent className="w-64">
														{phone && (
															<DropdownMenuItem asChild>
																<a href={`tel:${phone}`}>
																	<Phone />
																	<span>{t("contactMenu.callShort")}</span>
																	<DropdownMenuShortcut className="tracking-normal text-muted-foreground">
																		{phone}
																	</DropdownMenuShortcut>
																</a>
															</DropdownMenuItem>
														)}

														{phone && (
															<DropdownMenuItem onSelect={() => copyToClipboard(phone)}>
																<Copy />
																<span>{t("contactMenu.copyPhone")}</span>
															</DropdownMenuItem>
														)}

														{email && (
															<DropdownMenuItem onSelect={() => copyToClipboard(email)}>
																<Copy />
																<span>{t("contactMenu.copyEmail")}</span>
															</DropdownMenuItem>
														)}

														{!phone && !email && (
															<DropdownMenuItem disabled>
																{t("contactMenu.noContactDetails")}
															</DropdownMenuItem>
														)}
													</DropdownMenuSubContent>
												</DropdownMenuSub>
											);
										})
									)}
								</DropdownMenuSubContent>
							</DropdownMenuSub>

							{customerTagTypes.length > 0 && (
								<TagToggleSubMenu
									label={t("contactMenu.tags")}
									tagTypes={customerTagTypes}
									assignedTags={customer.tags ?? []}
									onToggle={handleToggleCustomerTag}
								/>
							)}
						</>
					)
				) : isLoading || !contactSummary ? (
					<div className="flex justify-center px-2 py-3">
						<LoadingIndicator size="s" showText={false} />
					</div>
				) : (
					<>
						<DropdownMenuLabel className="flex items-center justify-between gap-4">
							<span className="truncate">
								{contactSummary.firstName} {contactSummary.lastName}
							</span>
							{contactSummary.employeeNumber ? (
								<span className="shrink-0 text-xs font-normal text-muted-foreground">
									#{contactSummary.employeeNumber}
								</span>
							) : null}
						</DropdownMenuLabel>

						<DropdownMenuSeparator />

						<DropdownMenuItem asChild>
							<a href={getProfileRoute()}>
								<User />
								<span>{t("contactMenu.gotoProfile")}</span>
							</a>
						</DropdownMenuItem>

						{contactSummary.phoneNumber && (
							<DropdownMenuItem asChild>
								<a href={`tel:${contactSummary.phoneNumber}`}>
									<Phone />
									<span>{t("contactMenu.callShort")}</span>
									<DropdownMenuShortcut className="tracking-normal text-muted-foreground">
										{contactSummary.phoneNumber}
									</DropdownMenuShortcut>
								</a>
							</DropdownMenuItem>
						)}

						<DropdownMenuItem onSelect={handleOpenMessageModal}>
							<MessageSquare />
							<span>{t("contactMenu.message")}</span>
						</DropdownMenuItem>

						{contactSummary.role === "consultant" && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
									{t("contactMenu.settings")}
								</DropdownMenuLabel>

								<DropdownMenuItem onSelect={handleOpenAbsenceModal}>
									<CalendarX2 />
									<span>{t("REGISTER_ABSENCE")}</span>
								</DropdownMenuItem>

								{canShowTags && (
									<TagToggleSubMenu
										label={t("contactMenu.tags")}
										tagTypes={consultantTagTypes}
										assignedTags={contactSummary?.tags ?? []}
										onToggle={handleToggleTag}
										onRenew={handleRenewTag}
										onRemove={handleRemoveTag}
									/>
								)}
							</>
						)}
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ContactContextMenu;
